// Large decorative studio gear silhouettes. Drop as absolute accents
// behind sections to add cinematic flare without cluttering content.
import React from "react";

const baseProps = {
  fill: "none",
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

// Studio condenser microphone — long-form podcast mic silhouette.
export const StudioMic = ({ className = "", color = "#FFFFFF", strokeWidth = 1.5, ...props }) => (
  <svg viewBox="0 0 200 320" className={className} {...baseProps} stroke={color} strokeWidth={strokeWidth} {...props}>
    {/* shock mount cradle */}
    <ellipse cx="100" cy="80" rx="78" ry="86" />
    <line x1="22" y1="80" x2="2" y2="80" />
    <line x1="178" y1="80" x2="198" y2="80" />
    {/* mic body */}
    <rect x="62" y="34" width="76" height="120" rx="20" />
    {/* grille */}
    <line x1="78" y1="56" x2="122" y2="56" />
    <line x1="78" y1="72" x2="122" y2="72" />
    <line x1="78" y1="88" x2="122" y2="88" />
    <line x1="78" y1="104" x2="122" y2="104" />
    <line x1="78" y1="120" x2="122" y2="120" />
    {/* mid-band */}
    <line x1="62" y1="138" x2="138" y2="138" />
    {/* logo dot */}
    <circle cx="100" cy="146" r="3" fill={color} />
    {/* stem */}
    <line x1="100" y1="166" x2="100" y2="220" />
    {/* base mount swivel */}
    <circle cx="100" cy="226" r="8" />
    {/* yoke arm */}
    <path d="M40 240 L100 226 L160 240" />
    <line x1="40" y1="240" x2="40" y2="296" />
    <line x1="160" y1="240" x2="160" y2="296" />
    <path d="M30 296 L170 296" />
    <rect x="30" y="296" width="140" height="16" />
  </svg>
);

// Over-ear studio headphones
export const Headphones = ({ className = "", color = "#FFFFFF", strokeWidth = 1.5, ...props }) => (
  <svg viewBox="0 0 280 220" className={className} {...baseProps} stroke={color} strokeWidth={strokeWidth} {...props}>
    {/* headband */}
    <path d="M30 130 C30 50, 250 50, 250 130" />
    <path d="M30 130 C30 60, 250 60, 250 130" />
    {/* left cup */}
    <rect x="6" y="120" width="58" height="80" rx="14" />
    <rect x="14" y="130" width="42" height="60" rx="8" />
    <circle cx="35" cy="160" r="14" />
    <circle cx="35" cy="160" r="6" fill={color} />
    {/* right cup */}
    <rect x="216" y="120" width="58" height="80" rx="14" />
    <rect x="224" y="130" width="42" height="60" rx="8" />
    <circle cx="245" cy="160" r="14" />
    <circle cx="245" cy="160" r="6" fill={color} />
    {/* hinge dots */}
    <circle cx="35" cy="120" r="3" fill={color} />
    <circle cx="245" cy="120" r="3" fill={color} />
    {/* logo line on band */}
    <line x1="120" y1="62" x2="160" y2="62" />
  </svg>
);

// Cinema camera silhouette (RED-style block)
export const CinemaCamera = ({ className = "", color = "#FFFFFF", strokeWidth = 1.5, ...props }) => (
  <svg viewBox="0 0 320 200" className={className} {...baseProps} stroke={color} strokeWidth={strokeWidth} {...props}>
    {/* body */}
    <rect x="40" y="50" width="140" height="100" rx="6" />
    {/* lens mount */}
    <rect x="180" y="68" width="14" height="64" />
    {/* lens */}
    <rect x="194" y="58" width="100" height="84" rx="6" />
    <circle cx="244" cy="100" r="32" />
    <circle cx="244" cy="100" r="22" />
    <circle cx="244" cy="100" r="10" />
    <circle cx="244" cy="100" r="3" fill={color} />
    {/* top handle */}
    <path d="M70 50 L70 28 L150 28 L150 50" />
    {/* top connectors */}
    <line x1="85" y1="28" x2="85" y2="20" />
    <line x1="135" y1="28" x2="135" y2="20" />
    {/* viewfinder */}
    <rect x="30" y="60" width="14" height="34" />
    {/* record indicator */}
    <circle cx="60" cy="80" r="5" fill={color} />
    {/* status display */}
    <rect x="80" y="100" width="46" height="22" />
    <line x1="86" y1="108" x2="120" y2="108" />
    <line x1="86" y1="115" x2="110" y2="115" />
    {/* base/rails */}
    <line x1="40" y1="150" x2="180" y2="150" />
    <line x1="60" y1="150" x2="60" y2="166" />
    <line x1="160" y1="150" x2="160" y2="166" />
  </svg>
);

// Mic with cable curls — a more "on-stage" mic.
export const HandMic = ({ className = "", color = "#FFFFFF", strokeWidth = 1.5, ...props }) => (
  <svg viewBox="0 0 160 320" className={className} {...baseProps} stroke={color} strokeWidth={strokeWidth} {...props}>
    {/* mic head */}
    <ellipse cx="80" cy="70" rx="46" ry="60" />
    {/* grille */}
    {[40, 56, 72, 88, 104].map((y) => (
      <line key={y} x1="46" y1={y} x2="114" y2={y} />
    ))}
    {/* handle */}
    <rect x="64" y="130" width="32" height="140" rx="8" />
    {/* ring */}
    <line x1="64" y1="146" x2="96" y2="146" />
    <line x1="64" y1="162" x2="96" y2="162" />
    {/* cable */}
    <path d="M80 270 C 60 280, 120 290, 80 304 S 60 314, 80 318" />
  </svg>
);

// Vinyl record
export const Vinyl = ({ className = "", color = "#FFFFFF", strokeWidth = 1.5, ...props }) => (
  <svg viewBox="0 0 200 200" className={className} {...baseProps} stroke={color} strokeWidth={strokeWidth} {...props}>
    <circle cx="100" cy="100" r="92" />
    <circle cx="100" cy="100" r="80" />
    <circle cx="100" cy="100" r="70" />
    <circle cx="100" cy="100" r="60" />
    <circle cx="100" cy="100" r="50" />
    <circle cx="100" cy="100" r="36" />
    <circle cx="100" cy="100" r="10" fill={color} />
  </svg>
);
